import React, { useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Container, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, IconButton, Snackbar, Slider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import EditIcon from '@mui/icons-material/Edit';
import TokenIcon from '../components/TokenIcon';
import { usePrices } from '../hooks/usePrices';
import TransactionsList from '../components/TransactionsList';
import CoinChart from '../components/CoinChart';
import { getPortfolio, getOrders, placeOrder, cancelOrder, getLeaderboard, getCoins, getTransactions, getTraderTransactions, getTraderPortfolio } from '../services/vtradingApi';
import { getUserInfo, updatePublicName } from '../services/api';
import ApexChart from 'react-apexcharts';
import { getPortfolioValueHistory } from '../services/vtradingApi';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { formatBalance } from '../utils/format';
import { useTranslation } from 'react-i18next';
import { isRTL } from '../i18n';

const pinkTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#e91e63' },
    background: { default: '#fff0f6' },
  },
});

const orderTypes = [
  { value: 'market', label: 'Market' },
  { value: 'limit', label: 'Limit' }
];
const types = [
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' }
];

export default function VPortfolioPage() {
  const { t } = useTranslation();
  // DEBUG: Log tab translations
  const { navigateWithLang } = useLanguageNavigation();
  const [tab, setTab] = useState(0);

  // User state
  const [userInfo, setUserInfo] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState(null);

  // Public name editing state
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [newPublicName, setNewPublicName] = useState('');
  const [updatingName, setUpdatingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Portfolio state
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [portfolioError, setPortfolioError] = useState(null);

  // Transactions state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);

  // Orders state
  const [orders, setOrders] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [openOrder, setOpenOrder] = useState(false);
  const [orderForm, setOrderForm] = useState({ coin_id: '', type: 'buy', order_type: 'market', price: '', amount: '' });
  const [placing, setPlacing] = useState(false);
  const [canceling, setCanceling] = useState(null);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);

  // Trader modal state
  const [traderModalOpen, setTraderModalOpen] = useState(false);
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [traderTab, setTraderTab] = useState(0); // 0 = portfolio, 1 = transactions
  const [traderTransactions, setTraderTransactions] = useState([]);
  const [traderTransactionsLoading, setTraderTransactionsLoading] = useState(false);
  const [traderTransactionsError, setTraderTransactionsError] = useState(null);
  const [traderPortfolio, setTraderPortfolio] = useState(null);
  const [traderPortfolioLoading, setTraderPortfolioLoading] = useState(false);
  const [traderPortfolioError, setTraderPortfolioError] = useState(null);

  // Order modal price state
  const [selectedCoinPrice, setSelectedCoinPrice] = useState(null);
  const [orderValue, setOrderValue] = useState(0);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [sliderValue, setSliderValue] = useState(10);

  // TODO: Replace with real coin list from backend
  const [coins, setCoins] = useState([]);
  const [coinsLoading, setCoinsLoading] = useState(true);
  const [coinsError, setCoinsError] = useState(null);

  // 2. Add state for value history
  const [valueHistory, setValueHistory] = useState([]);
  const [valueHistoryLoading, setValueHistoryLoading] = useState(true);
  const [valueHistoryError, setValueHistoryError] = useState(null);

  // Fetch user info
  useEffect(() => {
    setUserLoading(true);
    getUserInfo()
      .then(data => {
        setUserInfo(data.user);
        setNewPublicName(data.user.public_name || '');
      })
      .catch(e => setUserError(e.message))
      .finally(() => setUserLoading(false));
  }, []);

  // Fetch portfolio
  const fetchPortfolio = () => {
    setPortfolioLoading(true);
    setPortfolioError(null);
    getPortfolio()
      .then(setPortfolio)
      .catch(e => setPortfolioError(e.message))
      .finally(() => setPortfolioLoading(false));
  };

  useEffect(() => {
    fetchPortfolio();
  }, []);

  // Fetch transactions
  const fetchTransactions = () => {
    setTransactionsLoading(true);
    getTransactions()
      .then(data => setTransactions(data.transactions || []))
      .catch(e => setTransactionsError(e.message))
      .finally(() => setTransactionsLoading(false));
  };
  useEffect(() => {
    if (tab === 1 && transactions.length === 0 && !transactionsLoading) fetchTransactions();
    // eslint-disable-next-line
  }, [tab]);

  // Fetch orders
  const fetchOrders = () => {
    setOrdersLoading(true);
    getOrders()
      .then(setOrders)
      .catch(e => setOrdersError(e.message))
      .finally(() => setOrdersLoading(false));
  };
  useEffect(() => {
    if (tab === 2 && !orders) fetchOrders();
    // eslint-disable-next-line
  }, [tab]);

  // Fetch leaderboard
  useEffect(() => {
    if (tab === 3 && !leaderboard) {
      setLeaderboardLoading(true);
      getLeaderboard()
        .then(setLeaderboard)
        .catch(e => setLeaderboardError(e.message))
        .finally(() => setLeaderboardLoading(false));
    }
    // eslint-disable-next-line
  }, [tab]);

  // Fetch coins for order form
  useEffect(() => {
    setCoinsLoading(true);
    getCoins()
      .then(setCoins)
      .catch(e => setCoinsError(e.message))
      .finally(() => setCoinsLoading(false));
  }, []);

  // Recalculate available balance when orders are loaded
  useEffect(() => {
    if (orders && orderForm.coin_id) {
      const selectedCoin = coins.find(c => c.id === orderForm.coin_id);
      if (selectedCoin) {
        updateAvailableBalance(selectedCoin.symbol, orderForm.type);
      }
    }
  }, [orders, orderForm.coin_id, orderForm.type]);

  // 3. Fetch value history on mount
  useEffect(() => {
    setValueHistoryLoading(true);
    setValueHistoryError(null);
    getPortfolioValueHistory()
      .then(data => setValueHistory(data.valueHistory || []))
      .catch(e => setValueHistoryError(e.message))
      .finally(() => setValueHistoryLoading(false));
  }, []);

  // Public name editing handlers
  const handleEditNameOpen = () => {
    setNewPublicName(userInfo?.public_name || '');
    setNameError('');
    setEditNameOpen(true);
  };

  const handleEditNameClose = () => {
    setEditNameOpen(false);
    setNameError('');
  };

  const handleUpdatePublicName = async () => {
    if (!newPublicName.trim()) {
      setNameError('Public name is required');
      return;
    }

    setUpdatingName(true);
    setNameError('');
    
    try {
      await updatePublicName(newPublicName.trim());
      setUserInfo(prev => ({ ...prev, public_name: newPublicName.trim() }));
      setEditNameOpen(false);
      setSnackbar({ open: true, message: 'Public name updated successfully!', severity: 'success' });
    } catch (error) {
      setNameError(error.response?.data?.error || error.message || 'Failed to update public name');
    } finally {
      setUpdatingName(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Order form handlers
  const handleOrderOpen = () => {
    setOpenOrder(true);
    // Fetch orders to get pending orders for balance calculation
    fetchOrders();
  };
  const handleOrderClose = () => {
    setOpenOrder(false);
    setSelectedCoinPrice(null);
    setOrderValue(0);
    setAvailableBalance(0);
    setSliderValue(10);
  };
  
  const handleOrderChange = e => {
    const { name, value } = e.target;
    setOrderForm(f => ({ ...f, [name]: value }));
    
    // Update price and value when coin changes
    if (name === 'coin_id' && value) {
      const selectedCoin = coins.find(c => c.id === value);
      if (selectedCoin) {
        setSelectedCoinPrice(selectedCoin.current_price);
        // Reset value calculation
        setOrderValue(0);
        updateAvailableBalance(selectedCoin.symbol, orderForm.type);
      }
    }
    
    // Update balance when order type changes
    if (name === 'type') {
      if (orderForm.coin_id) {
        const selectedCoin = coins.find(c => c.id === orderForm.coin_id);
        if (selectedCoin) {
          updateAvailableBalance(selectedCoin.symbol, value);
        }
      }
    }
    
    // Update balance when orders are loaded (for pending order calculation)
    if (name === 'coin_id' && value && orders) {
      const selectedCoin = coins.find(c => c.id === value);
      if (selectedCoin) {
        updateAvailableBalance(selectedCoin.symbol, orderForm.type);
      }
    }
    
    // Update value when amount changes
    if (name === 'amount' && value && selectedCoinPrice) {
      const amount = Number(value);
      const calculatedValue = amount * selectedCoinPrice;
      setOrderValue(calculatedValue);
    } else if (name === 'amount' && !value) {
      setOrderValue(0);
    }
  };

  const updateAvailableBalance = (coinSymbol, orderType) => {
    if (!portfolio || !portfolio.holdings) return;
    
    if (orderType === 'buy') {
      // For buy orders, show available USDT (total - reserved in pending orders)
      const usdtHolding = portfolio.holdings.find(h => h.symbol === 'USDT');
      const totalUsdt = usdtHolding ? Number(usdtHolding.amount) : 0;
      
      // Calculate reserved USDT from pending buy orders
      let reservedUsdt = 0;
      if (orders && orders.pending) {
        orders.pending.forEach(order => {
          if (order.type === 'buy') {
            const orderCost = Number(order.amount) * Number(order.price || order.current_price || 0);
            reservedUsdt += orderCost;
          }
        });
      }
      
      const availableUsdt = totalUsdt - reservedUsdt;
      setAvailableBalance(Math.max(0, availableUsdt));
    } else {
      // For sell orders, show available coin amount (total - reserved in pending sell orders)
      const coinHolding = portfolio.holdings.find(h => h.symbol === coinSymbol);
      const totalCoin = coinHolding ? Number(coinHolding.amount) : 0;
      
      // Calculate reserved coin from pending sell orders for this specific coin
      let reservedCoin = 0;
      if (orders && orders.pending) {
        orders.pending.forEach(order => {
          if (order.type === 'sell' && order.symbol === coinSymbol) {
            reservedCoin += Number(order.amount);
          }
        });
      }
      
      const availableCoin = totalCoin - reservedCoin;
      setAvailableBalance(Math.max(0, availableCoin));
    }
  };

  const handleSliderChange = (event, newValue) => {
    setSliderValue(newValue);
    
    if (orderForm.type === 'buy') {
      // For buy orders, calculate amount based on USDT percentage
      const usdtAmount = (availableBalance * newValue) / 100;
      const coinPrice = selectedCoinPrice || 0;
      const coinAmount = coinPrice > 0 ? usdtAmount / coinPrice : 0;
      setOrderForm(prev => ({ ...prev, amount: coinAmount.toFixed(8) }));
      setOrderValue(usdtAmount);
    } else {
      // For sell orders, calculate amount based on coin percentage
      const coinAmount = (availableBalance * newValue) / 100;
      setOrderForm(prev => ({ ...prev, amount: coinAmount.toFixed(8) }));
      const coinPrice = selectedCoinPrice || 0;
      setOrderValue(coinAmount * coinPrice);
    }
  };

  // Validate amount input to prevent exceeding available balance
  const validateAmount = (amount, type, coinSymbol) => {
    if (!amount || Number(amount) <= 0) return t('vportfolio.invalidAmount');
    
    if (type === 'buy') {
      const cost = Number(amount) * (selectedCoinPrice || 0);
      
      if (cost > availableBalance) {
        return `Insufficient USDT. You have ${availableBalance.toFixed(2)} USDT available but need ${cost.toFixed(2)} USDT`;
      }
    } else if (type === 'sell') {
      if (Number(amount) > availableBalance) {
        return `Insufficient ${coinSymbol}. You have ${availableBalance.toFixed(8)} ${coinSymbol} available but trying to sell ${Number(amount).toFixed(8)}`;
      }
    }
    
    return null;
  };
  const handlePlaceOrder = async () => {
    setPlacing(true);
    setOrdersError(null);
    
    try {
      // Client-side validation
      const { coin_id, type, order_type, price, amount } = orderForm;
      
      if (!coin_id || !amount || Number(amount) <= 0) {
        throw new Error(t('vportfolio.invalidAmount'));
      }
      
      const selectedCoin = coins.find(c => c.id === coin_id);
      if (!selectedCoin) {
        throw new Error('Selected coin not found');
      }
      
      const numAmount = Number(amount);
      const numPrice = order_type === 'market' ? selectedCoinPrice : Number(price);
      
      if (order_type === 'limit' && (!numPrice || numPrice <= 0)) {
        throw new Error('Please enter a valid price for limit orders');
      }
      
      // Check balance based on order type
      if (type === 'buy') {
        const cost = numAmount * numPrice;
        
        if (cost > availableBalance) {
          throw new Error(`Insufficient USDT balance. You have ${availableBalance.toFixed(2)} USDT available but need ${cost.toFixed(2)} USDT`);
        }
      } else if (type === 'sell') {
        if (numAmount > availableBalance) {
          throw new Error(`Insufficient ${selectedCoin.symbol} balance. You have ${availableBalance.toFixed(8)} ${selectedCoin.symbol} available but trying to sell ${numAmount.toFixed(8)}`);
        }
      }
      
      // Proceed with order placement
      await placeOrder({ ...orderForm, price: orderForm.order_type === 'market' ? undefined : orderForm.price });
      setOpenOrder(false);
      setOrderForm({ coin_id: '', type: 'buy', order_type: 'market', price: '', amount: '' });
      fetchOrders();
      // Refresh transactions to show the new transaction from the executed order
      fetchTransactions();
      // Refresh portfolio to show updated holdings
      fetchPortfolio();
    } catch (e) {
      setOrdersError(e.message);
    } finally {
      setPlacing(false);
    }
  };
  const handleCancelOrder = async (orderId) => {
    setCanceling(orderId);
    setOrdersError(null);
    try {
      await cancelOrder(orderId);
      fetchOrders();
    } catch (e) {
      setOrdersError(e.message);
    } finally {
      setCanceling(null);
    }
  };

  // Trader modal handlers
  const handleTraderClick = async (trader) => {
    setSelectedTrader(trader);
    setTraderModalOpen(true);
    setTraderTab(0); // Start with portfolio tab
    
    // Fetch trader portfolio
    setTraderPortfolioLoading(true);
    setTraderPortfolioError(null);
    try {
      const portfolioData = await getTraderPortfolio(trader.user_id);
      setTraderPortfolio(portfolioData);
    } catch (error) {
      setTraderPortfolioError(error.message || 'Failed to load trader portfolio');
    } finally {
      setTraderPortfolioLoading(false);
    }
    
    // Fetch trader transactions
    setTraderTransactionsLoading(true);
    setTraderTransactionsError(null);
    try {
      const transactionsData = await getTraderTransactions(trader.user_id);
      setTraderTransactions(transactionsData.transactions || []);
    } catch (error) {
      setTraderTransactionsError(error.message || 'Failed to load trader transactions');
    } finally {
      setTraderTransactionsLoading(false);
    }
  };

  const handleTraderModalClose = () => {
    setTraderModalOpen(false);
    setSelectedTrader(null);
    setTraderTab(0);
    setTraderTransactions([]);
    setTraderTransactionsError(null);
    setTraderPortfolio(null);
    setTraderPortfolioError(null);
  };

  // Portfolio: get coin symbols for usePrices
  const portfolioSymbols = React.useMemo(() => {
    if (!portfolio || !portfolio.holdings) return [];
    return portfolio.holdings.map(h => h.symbol).filter(Boolean);
  }, [portfolio]);
  const { prices: portfolioPrices, loading: pricesLoading } = usePrices(portfolioSymbols, true, 'VPortfolioPage');

  // Transactions: get coin symbols for usePrices
  const transactionSymbols = React.useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    return [...new Set(transactions.map(t => t.symbol || t.coin).filter(Boolean))];
  }, [transactions]);
  const { prices: transactionPrices } = usePrices(transactionSymbols, true, 'VPortfolioPage-Transactions');

  // Helper for formatting currency
  const formatCurrency = (value) => {
    if (typeof value !== 'number') value = Number(value) || 0;
    return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Click handler for navigating to coin page
  const handleRowClick = (coinSymbol) => {
            navigateWithLang(`coin/${coinSymbol}`);
  };

  // Portfolio table columns (match real wallet)
  const portfolioColumns = [
    { id: 'symbol', label: t('tables.portfolio.symbol'), align: 'left' },
    { id: 'amount', label: t('tables.portfolio.amount'), align: 'right' },
    { id: 'price', label: t('tables.portfolio.currentPrice'), align: 'right' },
    { id: 'value', label: t('tables.portfolio.marketValue'), align: 'right' },
  ];

  return (
    <ThemeProvider theme={pinkTheme}>
      <Container maxWidth="lg" sx={{ mt: 2, mb: 2, px: { xs: 0.5, sm: 1, md: 2 } }}>
        <Box sx={{ bgcolor: 'background.default', p: { xs: 1, sm: 2 }, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', mb: 3 }}>
            <Typography variant="h4" gutterBottom color="primary">
              {t('vportfolio.title')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {userLoading ? (
                <CircularProgress size={20} />
              ) : userError ? (
                <Typography color="error">{userError}</Typography>
              ) : userInfo ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" color="text.secondary">
                    {userInfo.public_name || t('vportfolio.anonymousTrader')}
                  </Typography>
                  <IconButton size="small" onClick={handleEditNameOpen} sx={{ color: 'primary.main', ml: 0.5 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {portfolio && (
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary', ml: 3 }}>
                      {formatCurrency(portfolio.totalValue)}
                    </Typography>
                  )}
                </Box>
              ) : null}
            </Box>
          </Box>

          {/* Place Order Button above tabs, visible for tabs 0, 1, 2 */}
          {tab !== 3 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
              <Button 
                variant="contained" 
                onClick={handleOrderOpen} 
                disabled={coinsLoading}
                size="small"
                sx={{ borderRadius: '999px', px: 2, py: 0.5, fontSize: '0.95rem', minHeight: 28, height: 32 }}
              >
                {t('vportfolio.trade')}
              </Button>
            </Box>
          )}

          {/* Total Value Display - Available on all tabs */}
          {portfolioLoading && <CircularProgress />}
          {portfolioError && <Alert severity="error">{portfolioError}</Alert>}
          

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
            {[
              { label: t('vportfolio.tabs.portfolio'), value: 0 },
              { label: t('vportfolio.tabs.trans'), value: 1 },
              { label: t('vportfolio.tabs.orders'), value: 2 },
              { label: <><EmojiEventsIcon sx={{ fontSize: 18, mr: 0.5, mb: '-2px' }} />{t('vportfolio.tabs.leaders')}</>, value: 3 },
            ].map(({ label, value }) => (
              <Button
                key={label}
                onClick={() => setTab(value)}
                variant={tab === value ? 'contained' : 'text'}
                color={tab === value ? 'primary' : 'inherit'}
                sx={{
                  borderRadius: '16px 16px 0 0',
                  fontWeight: tab === value ? 700 : 500,
                  textTransform: 'none',
                  bgcolor: tab === value ? 'primary.main' : 'transparent',
                  color: tab === value ? '#fff' : 'text.secondary',
                  boxShadow: tab === value ? 2 : 0,
                  borderBottom: tab === value ? '2px solid #e91e63' : '2px solid transparent',
                  minWidth: 0,
                  mb: tab === value ? '-2px' : 0,
                  px: 1,
                  py: 1,
                  fontSize: { xs: '0.97rem', sm: '1rem' },
                  '&:hover': {
                    bgcolor: tab === value ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>

          {/* Portfolio Tab */}
          {tab === 0 && (
            <>
              {portfolioLoading && <CircularProgress />}
              {portfolioError && <Alert severity="error">{portfolioError}</Alert>}
              {portfolio && (
                <>
                  {/* Portfolio Value Chart */}
                  <Box sx={{ mb: 3 }}>
                    {valueHistoryLoading ? (
                      <CircularProgress />
                    ) : valueHistoryError ? (
                      <Alert severity="error">{valueHistoryError}</Alert>
                    ) : valueHistory && valueHistory.length > 0 ? (
                      <ApexChart
                        type="line"
                        height={160}
                        series={[{
                          name: 'Portfolio Value',
                          data: valueHistory.map((d) => ({ x: d.date, y: d.value }))
                        }]}
                        options={{
                          chart: {
                            id: 'portfolio-value-chart',
                            toolbar: { show: false },
                            zoom: { enabled: false },
                          },
                          xaxis: {
                            type: 'datetime',
                            labels: {
                              style: { fontSize: '12px', color: '#888' },
                              show: true,
                              rotate: 0,
                              datetimeUTC: false,
                              datetimeFormatter: {
                                year: 'MMM dd',
                                month: 'MMM dd',
                                day: 'MMM dd',
                                hour: 'HH:mm'
                              }
                            },
                            axisBorder: { show: false },
                            axisTicks: { show: false },
                            tooltip: { enabled: false },
                            crosshairs: { show: false },
                          },
                          yaxis: {
                            labels: {
                              formatter: val => '$' + val.toLocaleString(undefined, { maximumFractionDigits: 0 })
                            },
                            title: { text: 'Value (USD)' },
                          },
                          tooltip: {
                            y: {
                              formatter: val => '$' + val.toLocaleString(undefined, { maximumFractionDigits: 2 })
                            }
                          },
                          stroke: { curve: 'straight', width: 3 },
                          colors: ['#e91e63'],
                          grid: { borderColor: '#eee' },
                        }}
                      />
                    ) : (
                      <Alert severity="info">No value history available.</Alert>
                    )}
                  </Box>
                  {/* Portfolio Holdings Table */}
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', px: 0, mx: 0, overflowX: 'unset' }}>
                    <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          {portfolioColumns.map((column) => (
                            <TableCell key={column.id} align={column.align} sx={{
                              ...(column.id === 'symbol' && { width: '20%' }),
                              ...(column.id === 'amount' && { width: '20%' }),
                              ...(column.id === 'price' && { width: '30%' }),
                              ...(column.id === 'value' && { width: '30%' }),
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                              {column.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {portfolio.holdings
                          .map((holding) => {
                            const currentPrice = portfolioPrices[holding.symbol]?.usd || holding.price || 0;
                            const marketValue = Number(holding.amount) * currentPrice;
                            return { ...holding, currentPrice, marketValue };
                          })
                          .sort((a, b) => b.marketValue - a.marketValue)
                          .map((holding) => (
                            <TableRow 
                              key={holding.symbol}
                              onClick={() => handleRowClick(holding.symbol)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                                transition: 'background-color 0.2s ease'
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TokenIcon coinSymbol={holding.symbol} size={24} />
                                  {holding.symbol}
                                </Box>
                              </TableCell>
                              <TableCell align="right">{formatBalance(holding.amount)}</TableCell>
                              <TableCell align="right">{formatCurrency(holding.currentPrice)}</TableCell>
                              <TableCell align="right">${formatBalance(holding.marketValue)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}

          {/* Transactions Tab */}
          {tab === 1 && (
            <>
              {transactionsLoading && <CircularProgress />}
              {transactionsError && <Alert severity="error">{transactionsError}</Alert>}
              {transactions.length > 0 && (
                <TransactionsList 
                  transactions={transactions} 
                  prices={transactionPrices}
                  showWallet={false}
                  sortConfig={{ sortBy: 'date', sortOrder: 'desc' }}
                  onSort={() => {}} // No-op for virtual transactions
                  onPageChange={() => {}} // No-op for virtual transactions
                  formatCurrency={formatCurrency}
                  onRowClick={handleRowClick}
                />
              )}
              {!transactionsLoading && transactions.length === 0 && (
                <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  No transactions yet. Start trading to see your history!
                </Paper>
              )}
            </>
          )}

          {/* Orders Tab */}
          {tab === 2 && (
            <>
              
              {ordersLoading && <CircularProgress />}
              {ordersError && <Alert severity="error">{ordersError}</Alert>
              }
              {orders && (
                <>
                  {/* Pending Orders */}
                  <Typography variant="h6" sx={{ mb: 2 }}>{t('vportfolio.pendingOrders')}</Typography>
                  {orders.pending && orders.pending.length > 0 ? (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', px: 0, mx: 0 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('vportfolio.coin')}</TableCell>
                            <TableCell>{t('vportfolio.type')}</TableCell>
                            <TableCell>{t('vportfolio.orderType')}</TableCell>
                            <TableCell align="right">{t('vportfolio.buyPrice')}</TableCell>
                            <TableCell align="right">{t('vportfolio.amount')}</TableCell>
                            <TableCell align="right">{t('vportfolio.actions')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orders.pending.map((order) => (
                            <TableRow 
                              key={order.id}
                              onClick={() => handleRowClick(order.symbol)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                                transition: 'background-color 0.2s ease'
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TokenIcon coinSymbol={order.symbol} size={24} />
                                  {order.symbol}
                                </Box>
                              </TableCell>
                              <TableCell>{order.type}</TableCell>
                              <TableCell>{order.order_type}</TableCell>
                              <TableCell align="right">{order.price ? formatCurrency(order.price) : 'Market'}</TableCell>
                              <TableCell align="right">{Number(order.amount).toLocaleString(undefined, { maximumFractionDigits: 8 })}</TableCell>
                              <TableCell align="right">
                                <Button 
                                  size="small" 
                                  variant="outlined" 
                                  color="error"
                                  disabled={canceling === order.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCancelOrder(order.id);
                                  }}
                                >
                                  {canceling === order.id ? t('vportfolio.canceling') : t('vportfolio.cancel')}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Paper sx={{ p: 2, textAlign: 'center', color: 'text.secondary', mb: 3 }}>
                      No pending orders
                    </Paper>
                  )}

                  {/* Order History */}
                  <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>{t('vportfolio.orderHistory')}</Typography>
                  {orders.history && orders.history.length > 0 ? (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', px: 0, mx: 0 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('vportfolio.coin')}</TableCell>
                            <TableCell>{t('vportfolio.type')}</TableCell>
                            <TableCell>{t('vportfolio.orderType')}</TableCell>
                            <TableCell align="right">{t('vportfolio.buyPrice')}</TableCell>
                            <TableCell align="right">{t('vportfolio.amount')}</TableCell>
                            <TableCell>{t('vportfolio.status')}</TableCell>
                            <TableCell>{t('vportfolio.date')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {orders.history.map((order) => (
                            <TableRow 
                              key={order.id}
                              onClick={() => handleRowClick(order.symbol)}
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                                transition: 'background-color 0.2s ease'
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TokenIcon coinSymbol={order.symbol} size={24} />
                                  {order.symbol}
                                </Box>
                              </TableCell>
                              <TableCell>{order.type}</TableCell>
                              <TableCell>{order.order_type}</TableCell>
                              <TableCell align="right">{order.price ? formatCurrency(order.price) : 'Market'}</TableCell>
                              <TableCell align="right">{Number(order.amount).toLocaleString(undefined, { maximumFractionDigits: 8 })}</TableCell>
                              <TableCell>{order.status}</TableCell>
                              <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Paper sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                      No order history
                    </Paper>
                  )}
                </>
              )}
            </>
          )}

          {/* Leaderboard Tab */}
          {tab === 3 && (
            <>
              {leaderboardLoading && <CircularProgress />}
              {leaderboardError && <Alert severity="error">{leaderboardError}</Alert>}
              {leaderboard && (
                <TableContainer component={Paper} sx={{ boxShadow: 'none', px: 0, mx: 0 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={isRTL() ? { direction: 'rtl' } : {}}>
                        <TableCell align={isRTL() ? 'right' : 'left'} sx={{ textAlign: isRTL() ? 'right' : undefined }}>{t('vleaderboard.rank')}</TableCell>
                        <TableCell align={isRTL() ? 'right' : 'left'} sx={{ textAlign: isRTL() ? 'right' : undefined }}>{t('vleaderboard.publicName')}</TableCell>
                        <TableCell align={isRTL() ? 'right' : 'right'} sx={{ textAlign: isRTL() ? 'right' : undefined }}>{t('vleaderboard.portfolioValue')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leaderboard.leaderboard.map((u, i) => (
                        <TableRow 
                          key={u.user_id} 
                          onClick={() => handleTraderClick(u)}
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(233, 30, 99, 0.08)' }
                          }}
                        >
                          <TableCell align={isRTL() ? 'right' : 'left'} sx={{ textAlign: isRTL() ? 'right' : undefined }}>{i + 1}</TableCell>
                          <TableCell align={isRTL() ? 'right' : 'left'} sx={{ textAlign: isRTL() ? 'right' : undefined }}>{u.public_name}</TableCell>
                          <TableCell align={isRTL() ? 'right' : 'right'} sx={{ textAlign: isRTL() ? 'right' : undefined }}>${u.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
      </Container>

      {/* Edit Public Name Dialog */}
      <Dialog open={editNameOpen} onClose={handleEditNameClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('vportfolio.editPublicName')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('vportfolio.publicName')}
            fullWidth
            variant="outlined"
            value={newPublicName}
            onChange={(e) => setNewPublicName(e.target.value)}
            error={!!nameError}
            helperText={nameError || t('vportfolio.publicNameHelper')}
            inputProps={{ maxLength: 64 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditNameClose}>{t('common.cancel')}</Button>
          <Button 
            onClick={handleUpdatePublicName} 
            variant="contained" 
            disabled={updatingName || !newPublicName.trim()}
          >
            {updatingName ? t('vportfolio.updating') : t('vportfolio.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Dialog */}
      <Dialog open={openOrder} onClose={handleOrderClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('vportfolio.placeOrder')}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label={t('vportfolio.coin')}
            fullWidth
            name="coin_id"
            value={orderForm.coin_id}
            onChange={handleOrderChange}
            disabled={coinsLoading}
          >
            {coins.map((coin) => (
              <MenuItem key={coin.id} value={coin.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TokenIcon coinSymbol={coin.symbol} size={20} />
                  {coin.symbol} - {coin.name}
                </Box>
              </MenuItem>
            ))}
          </TextField>
          
          {/* Current Price and Balance Display */}
          {selectedCoinPrice && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('vportfolio.currentPrice', { price: formatCurrency(selectedCoinPrice) })}
              </Typography>
              {orderForm.type === 'buy' ? (
                // For buy orders, show USDT balance details
                (() => {
                  const usdtHolding = portfolio?.holdings?.find(h => h.symbol === 'USDT');
                  const totalUsdt = usdtHolding ? Number(usdtHolding.amount) : 0;
                  const reservedUsdt = totalUsdt - availableBalance;
                  
                  return (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total USDT: <strong>{totalUsdt.toFixed(2)}</strong>
                      </Typography>
                      {reservedUsdt > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Reserved in pending orders: <strong>{reservedUsdt.toFixed(2)}</strong>
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Available USDT: <strong>{availableBalance.toFixed(2)}</strong>
                      </Typography>
                    </Box>
                  );
                })()
              ) : (
                // For sell orders, show coin balance details
                (() => {
                  const coinSymbol = coins.find(c => c.id === orderForm.coin_id)?.symbol;
                  const coinHolding = portfolio?.holdings?.find(h => h.symbol === coinSymbol);
                  const totalCoin = coinHolding ? Number(coinHolding.amount) : 0;
                  const reservedCoin = totalCoin - availableBalance;
                  
                  return (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Total {coinSymbol}: <strong>{totalCoin.toFixed(8)}</strong>
                      </Typography>
                      {reservedCoin > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Reserved in pending orders: <strong>{reservedCoin.toFixed(8)}</strong>
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Available {coinSymbol}: <strong>{availableBalance.toFixed(8)}</strong>
                      </Typography>
                    </Box>
                  );
                })()
              )}
            </Box>
          )}
          
          <TextField
            select
            margin="dense"
            label={t('vportfolio.type')}
            fullWidth
            name="type"
            value={orderForm.type}
            onChange={handleOrderChange}
          >
            {types.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {t(`vportfolio.types.${type.value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t('vportfolio.orderType')}
            fullWidth
            name="order_type"
            value={orderForm.order_type}
            onChange={handleOrderChange}
          >
            {orderTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {t(`vportfolio.orderTypes.${type.value}`)}
              </MenuItem>
            ))}
          </TextField>
          {orderForm.order_type === 'limit' && (
            <TextField
              margin="dense"
              label={t('vportfolio.price')}
              fullWidth
              name="price"
              type="number"
              value={orderForm.price}
              onChange={handleOrderChange}
            />
          )}
          <TextField
            margin="dense"
            label={t('vportfolio.amount')}
            fullWidth
            name="amount"
            type="number"
            value={orderForm.amount}
            onChange={handleOrderChange}
            error={!!validateAmount(orderForm.amount, orderForm.type, coins.find(c => c.id === orderForm.coin_id)?.symbol)}
            helperText={validateAmount(orderForm.amount, orderForm.type, coins.find(c => c.id === orderForm.coin_id)?.symbol)}
          />
          
          {/* Quick Amount Slider */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('vportfolio.quickAmount', { percent: sliderValue, symbol: orderForm.type === 'buy' ? 'USDT' : coins.find(c => c.id === orderForm.coin_id)?.symbol })}
            </Typography>
            <Slider
              value={sliderValue}
              onChange={handleSliderChange}
              min={10}
              max={100}
              step={10}
              disabled={availableBalance <= 0}
              marks={[
                { 
                  value: 10, 
                  label: (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">10%</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {orderForm.type === 'buy' 
                          ? `${(availableBalance * 0.1).toFixed(2)} USDT`
                          : `${(availableBalance * 0.1).toFixed(8)} ${coins.find(c => c.id === orderForm.coin_id)?.symbol}`
                        }
                      </Typography>
                    </Box>
                  )
                },
                { 
                  value: 25, 
                  label: (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">25%</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {orderForm.type === 'buy' 
                          ? `${(availableBalance * 0.25).toFixed(2)} USDT`
                          : `${(availableBalance * 0.25).toFixed(8)} ${coins.find(c => c.id === orderForm.coin_id)?.symbol}`
                        }
                      </Typography>
                    </Box>
                  )
                },
                { 
                  value: 50, 
                  label: (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">50%</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {orderForm.type === 'buy' 
                          ? `${(availableBalance * 0.5).toFixed(2)} USDT`
                          : `${(availableBalance * 0.5).toFixed(8)} ${coins.find(c => c.id === orderForm.coin_id)?.symbol}`
                        }
                      </Typography>
                    </Box>
                  )
                },
                { 
                  value: 75, 
                  label: (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">75%</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {orderForm.type === 'buy' 
                          ? `${(availableBalance * 0.75).toFixed(2)} USDT`
                          : `${(availableBalance * 0.75).toFixed(8)} ${coins.find(c => c.id === orderForm.coin_id)?.symbol}`
                        }
                      </Typography>
                    </Box>
                  )
                },
                { 
                  value: 100, 
                  label: (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2">100%</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {orderForm.type === 'buy' 
                          ? `${availableBalance.toFixed(2)} USDT`
                          : `${availableBalance.toFixed(8)} ${coins.find(c => c.id === orderForm.coin_id)?.symbol}`
                        }
                      </Typography>
                    </Box>
                  )
                }
              ]}
              valueLabelDisplay="auto"
              sx={{ mt: 1 }}
            />
            {availableBalance <= 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('vportfolio.noAvailableBalance')}
              </Typography>
            )}
          </Box>

          
          {/* Total Value Display */}
          {orderValue > 0 && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="body2" color="white">
                Total Value: <strong>{formatCurrency(orderValue)}</strong>
              </Typography>
            </Box>
          )}

          {/* Coin Chart - Show when a coin is selected */}
          {orderForm.coin_id && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {coins.find(c => c.id === orderForm.coin_id)?.symbol} Price Chart
              </Typography>
              <CoinChart 
                symbol={coins.find(c => c.id === orderForm.coin_id)?.symbol} 
                avgBuyPrice={null} // No avg buy price for order modal
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOrderClose}>Cancel</Button>
          <Button onClick={handlePlaceOrder} variant="contained" disabled={placing}>
            {placing ? 'Placing...' : 'Place Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trader Modal */}
      <Dialog open={traderModalOpen} onClose={handleTraderModalClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          {selectedTrader ? `${selectedTrader.public_name}'s Portfolio` : 'Trader Portfolio'}
        </DialogTitle>
        <DialogContent>
          {/* Market Value Display */}
          {selectedTrader && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
              <Typography variant="h6" color="white" sx={{ fontWeight: 'bold' }}>
                Total Portfolio Value: {formatCurrency(selectedTrader.value)}
              </Typography>
            </Box>
          )}

          {/* Tabs */}
          {/* Removed Tabs and Tab components */}

          {/* Portfolio Tab */}
          {traderTab === 0 && (
            <>
              {traderPortfolioLoading && (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              )}
              {traderPortfolioError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {traderPortfolioError}
                </Alert>
              )}
              {!traderPortfolioLoading && !traderPortfolioError && traderPortfolio && (
                <>
                  {traderPortfolio.holdings && traderPortfolio.holdings.length > 0 ? (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', px: 0, mx: 0 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Coin</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Current Price</TableCell>
                            <TableCell align="right">Market Value</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {traderPortfolio.holdings.map((holding) => (
                            <TableRow key={holding.symbol}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TokenIcon coinSymbol={holding.symbol} size={24} />
                                  {holding.symbol}
                                </Box>
                              </TableCell>
                              <TableCell>{holding.name}</TableCell>
                              <TableCell align="right">
                                {Number(holding.amount).toLocaleString(undefined, { maximumFractionDigits: 8 })}
                              </TableCell>
                              <TableCell align="right">{formatCurrency(holding.price)}</TableCell>
                              <TableCell align="right">{formatCurrency(holding.value)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                      No portfolio holdings found for this trader.
                    </Paper>
                  )}
                </>
              )}
            </>
          )}

          {/* Transactions Tab */}
          {traderTab === 1 && (
            <>
              {traderTransactionsLoading && (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              )}
              {traderTransactionsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {traderTransactionsError}
                </Alert>
              )}
              {!traderTransactionsLoading && !traderTransactionsError && (
                <>
                  {traderTransactions.length > 0 ? (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', px: 0, mx: 0 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Coin</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell align="right">Buy/Sell Price</TableCell>
                            <TableCell>Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {traderTransactions
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((transaction) => (
                              <TableRow key={transaction.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TokenIcon coinSymbol={transaction.symbol || transaction.coin} size={24} />
                                    {transaction.symbol || transaction.coin}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ 
                                    color: transaction.type === 'buy' ? 'success.main' : 'error.main',
                                    fontWeight: 'bold'
                                  }}>
                                    {transaction.type?.toUpperCase()}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  {Number(transaction.amount).toLocaleString(undefined, { maximumFractionDigits: 8 })}
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(transaction.buy_price || transaction.price)}
                                </TableCell>
                                <TableCell>
                                  {new Date(transaction.date).toLocaleDateString()}
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                      No transactions found for this trader.
                    </Paper>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTraderModalClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbar.message}
      />
    </ThemeProvider>
  );
}